
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { ArrowLeft } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

export function MaleSymptoms({ onBack, onResultsUpdate }) {
  const { toast } = useToast();
  const [symptoms, setSymptoms] = useState({
    physical: {},
    sexual: {},
    psychological: {}
  });
  const [clinicalData, setClinicalData] = useState({
    age: "",
    hasSymptoms: false
  });
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  const symptomsList = {
    physical: [
      { id: "fatigue", label: "Fadiga" },
      { id: "muscleWeak", label: "Fraqueza Muscular" },
      { id: "weightGain", label: "Ganho de Peso" },
      { id: "jointPain", label: "Dores Articulares" },
      { id: "sweating", label: "Sudorese Noturna" }
    ],
    sexual: [
      { id: "libido", label: "Diminuição da Libido" },
      { id: "erection", label: "Disfunção Erétil" },
      { id: "orgasm", label: "Alteração do Orgasmo" },
      { id: "volume", label: "Redução do Volume Ejaculado" }
    ],
    psychological: [
      { id: "mood", label: "Alteração do Humor" },
      { id: "depression", label: "Depressão" },
      { id: "anxiety", label: "Ansiedade" },
      { id: "concentration", label: "Dificuldade de Concentração" },
      { id: "sleep", label: "Distúrbios do Sono" }
    ]
  };

  const handleSymptomChange = (category, id, severity) => {
    setSymptoms(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [id]: severity
      }
    }));
  };

  const calculateScore = () => {
    let totalPoints = 0;
    let maxPoints = 0;

    Object.entries(symptoms).forEach(([category, categorySymptoms]) => {
      Object.entries(categorySymptoms).forEach(([_, severity]) => {
        switch (severity) {
          case "Leve":
            totalPoints += 1;
            break;
          case "Moderado":
            totalPoints += 2;
            break;
          case "Intenso":
            totalPoints += 3;
            break;
        }
        maxPoints += 3;
      });
    });

    return maxPoints > 0 ? (totalPoints / maxPoints) * 100 : 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const calculatedScore = calculateScore();
    setScore(calculatedScore);
    setShowResults(true);

    const results = {
      score: calculatedScore,
      symptoms,
      clinicalData,
      diagnosis: getDiagnosis(calculatedScore),
      recommendations: getRecommendations(calculatedScore)
    };

    if (onResultsUpdate) onResultsUpdate(results);

    toast({
      title: "Análise Concluída",
      description: "Seus resultados estão prontos",
    });
  };

  const getDiagnosis = (score) => {
    if (score >= 70) {
      return {
        level: "Alto",
        description: "Alta probabilidade de hipogonadismo. Avaliação laboratorial necessária.",
        type: "Possível Hipogonadismo",
        recommendations: [
          "Dosagem urgente de testosterona total e livre",
          "Avaliação do eixo hipotálamo-hipófise-gonadal",
          "Consulta com endocrinologista"
        ]
      };
    } else if (score >= 50) {
      return {
        level: "Moderado",
        description: "Sintomas sugestivos de andropausa/hipogonadismo tardio.",
        type: "Possível Andropausa",
        recommendations: [
          "Avaliação hormonal recomendada",
          "Investigação de causas secundárias",
          "Acompanhamento médico regular"
        ]
      };
    } else {
      return {
        level: "Baixo",
        description: "Sintomas podem ter outras causas.",
        type: "Improvável Hipogonadismo",
        recommendations: [
          "Avaliação clínica geral",
          "Investigação de outras causas",
          "Monitoramento dos sintomas"
        ]
      };
    }
  };

  const getRecommendations = (score) => {
    const baseRecommendations = [
      "Avaliação completa do perfil hormonal",
      "Exames laboratoriais específicos",
      "Acompanhamento médico especializado"
    ];

    if (score >= 70) {
      return [
        ...baseRecommendations,
        "Testosterona total e livre",
        "FSH e LH",
        "Estradiol",
        "Prolactina",
        "Avaliação metabólica completa"
      ];
    } else if (score >= 50) {
      return [
        ...baseRecommendations,
        "Testosterona total",
        "Perfil lipídico",
        "Glicemia de jejum"
      ];
    }

    return baseRecommendations;
  };

  return (
    <motion.div
      className="mt-20 max-w-4xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center gap-4 mb-6">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-[#ff3366] hover:text-[#ff3366]/80 hover:bg-[#1a1a2e]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <h2 className="text-xl font-bold text-[#ff3366]">
          Avaliação de Sintomas Masculinos
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Dados Clínicos */}
        <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
          <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
            Dados Clínicos
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <input
              type="number"
              placeholder="Idade"
              value={clinicalData.age}
              onChange={(e) => setClinicalData(prev => ({ ...prev, age: e.target.value }))}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              required
            />
          </div>
        </div>

        {/* Sintomas */}
        {Object.entries(symptomsList).map(([category, items]) => (
          <div key={category} className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
            <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
              {category === "physical" ? "Sintomas Físicos" :
               category === "sexual" ? "Sintomas Sexuais" : "Sintomas Psicológicos"}
            </h3>
            <div className="space-y-4">
              {items.map(({ id, label }) => (
                <div key={id} className="space-y-2">
                  <p className="text-white">{label}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {["Leve", "Moderado", "Intenso"].map((severity) => (
                      <div key={severity} className="flex items-center gap-2">
                        <Checkbox
                          id={`${id}-${severity}`}
                          checked={symptoms[category][id] === severity}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleSymptomChange(category, id, severity);
                            } else {
                              handleSymptomChange(category, id, null);
                            }
                          }}
                        />
                        <label htmlFor={`${id}-${severity}`} className="text-white/80">
                          {severity}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <Button
          type="submit"
          className="w-full bg-[#ff3366] hover:bg-[#ff3366]/80 text-white"
        >
          Analisar Sintomas
        </Button>
      </form>

      {showResults && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 space-y-6"
        >
          {/* Score e Diagnóstico */}
          <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
            <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
              Resultado da Avaliação
            </h3>
            <div className={`p-4 rounded-lg ${
              score >= 70
                ? "bg-red-500/20 border-red-500/40"
                : score >= 50
                  ? "bg-yellow-500/20 border-yellow-500/40"
                  : "bg-green-500/20 border-green-500/40"
            }`}>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-xl font-bold text-white">
                  Score: {score.toFixed(1)}%
                </h4>
                <span className="text-white/80">
                  {score >= 70 ? "Alto Risco" :
                   score >= 50 ? "Risco Moderado" : "Baixo Risco"}
                </span>
              </div>
              <p className="text-white/80">
                {getDiagnosis(score).description}
              </p>
            </div>
          </div>

          {/* Recomendações */}
          <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
            <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
              Recomendações
            </h3>
            <ul className="space-y-2">
              {getRecommendations(score).map((rec, index) => (
                <li
                  key={index}
                  className="text-white/80 flex items-start gap-2"
                >
                  <span className="text-[#ff3366]">•</span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>

          {/* Referências */}
          <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
            <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
              Referências
            </h3>
            <ul className="space-y-2 text-sm text-white/60">
              <li>• Endocrine Society Guidelines (2018)</li>
              <li>• European Association of Urology Guidelines (2023)</li>
              <li>• International Society for the Study of the Aging Male</li>
              <li>• Brazilian Society of Endocrinology and Metabolism</li>
            </ul>
          </div>

          {/* Disclaimer */}
          <div className="p-4 rounded-lg bg-blue-500/20 border border-blue-500/40">
            <p className="text-sm text-blue-400">
              ⚠️ Esta avaliação é baseada em diretrizes médicas atuais e serve apenas como referência.
              Consulte sempre um profissional de saúde para diagnóstico e tratamento adequados.
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
